import { recruiterEnv } from '@/config/env';
import { getKeycloak } from '@/auth/keycloak-instance';
import { triggerDownload } from '@/shared/xlsx/download-xlsx';
import { EXPORT_TIMEOUT_MS } from '../program-export/export-timeout';

/**
 * DEV-268 — download da planilha consolidada do alinhamento, gerada pelo Core
 * e repassada pelo BFF (`GET /programs/:id/alignment-export`). Molde do
 * `download-program-base.ts` (EU-739): `fetch` cru (binário), nome vindo do
 * `Content-Disposition` (exposto no CORS, AC-21), fuso do viewer na query.
 */

/** Código nomeado do Core (`alignment_no_responses`, `program_not_found`…). */
export class AlignmentExportError extends Error {
  readonly name = 'AlignmentExportError';
  constructor(
    readonly status: number,
    readonly code: string | null
  ) {
    super(`Alignment export failed (status ${status}${code ? `, ${code}` : ''})`);
  }
}

export const ALIGNMENT_NO_RESPONSES_CODE = 'alignment_no_responses';
const FALLBACK_FILE_NAME = 'alinhamento.xlsx';

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
}

export interface AlignmentExportDownloadResult {
  fileName: string;
  /** Linhas de dados relatadas pelo backend; `null` = header ausente. */
  dataRows: number | null;
}

export async function downloadAlignmentExport(
  programId: string
): Promise<AlignmentExportDownloadResult> {
  const keycloak = getKeycloak();
  try {
    await keycloak.updateToken(30);
  } catch {
    // token expirado → o BFF responde 401/403; o toast de erro cobre.
  }
  const token = keycloak.token;

  const url = new URL(`${recruiterEnv.apiUrl}/programs/${programId}/alignment-export`);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (tz) url.searchParams.set('tz', tz);

  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    signal: AbortSignal.timeout(EXPORT_TIMEOUT_MS),
  });

  if (!res.ok) {
    const code = await res
      .json()
      .then((body: unknown) =>
        body && typeof body === 'object' && 'error' in body ? String(body.error) : null
      )
      .catch(() => null);
    throw new AlignmentExportError(res.status, code);
  }

  const blob = await res.blob();
  const fileName =
    filenameFromContentDisposition(res.headers.get('content-disposition')) ?? FALLBACK_FILE_NAME;
  const rowsHeader = res.headers.get('x-alignment-export-rows');
  triggerDownload(blob, fileName);
  return { fileName, dataRows: rowsHeader === null ? null : Number(rowsHeader) };
}
